import { useId, useRef, useState } from 'react';

import { cleanHeadingText } from '../utils/articleHeadings';

/**
 * A ":::tabs" block of an article (utils/tabBlocks.js): the tab titles in a
 * row, one panel shown at a time. Keyboard as in the WAI-ARIA tabs pattern -
 * arrow keys move between tabs, Home/End jump to the first/last.
 * `renderMarkdown` renders a panel's text the way the rest of the article is.
 */
function ArticleTabs({ intro, tabs, renderMarkdown }) {
  const [active, setActive] = useState(0);
  const baseId = useId();
  const tabRefs = useRef([]);

  const select = (index) => {
    const next = (index + tabs.length) % tabs.length;
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  const handleKeyDown = (event) => {
    const moves = {
      ArrowRight: active + 1,
      ArrowLeft: active - 1,
      Home: 0,
      End: tabs.length - 1,
    };
    if (event.key in moves) {
      event.preventDefault();
      select(moves[event.key]);
    }
  };

  return (
    <>
      {intro && renderMarkdown(intro, `${baseId}-intro`)}

      <div className="article-tabs">
        <div className="article-tabs__list" role="tablist" onKeyDown={handleKeyDown}>
          {tabs.map((tab, index) => (
            <button
              key={`${tab.title}-${index}`}
              ref={(element) => { tabRefs.current[index] = element; }}
              type="button"
              role="tab"
              id={`${baseId}-tab-${index}`}
              aria-selected={index === active}
              aria-controls={`${baseId}-panel-${index}`}
              tabIndex={index === active ? 0 : -1}
              className={`article-tabs__tab${index === active ? ' is-active' : ''}`}
              onClick={() => setActive(index)}
            >
              {cleanHeadingText(tab.title)}
            </button>
          ))}
        </div>

        {tabs.map((tab, index) => (
          <div
            key={`${tab.title}-${index}`}
            role="tabpanel"
            id={`${baseId}-panel-${index}`}
            aria-labelledby={`${baseId}-tab-${index}`}
            className="article-tabs__panel"
            hidden={index !== active}
          >
            {renderMarkdown(tab.text, `${baseId}-panel-${index}`)}
          </div>
        ))}
      </div>
    </>
  );
}

export default ArticleTabs;
